/**
 * ArcLog OS Icons — Lucide is the default system icon set.
 *
 * Host owns the library (curated tree-shaken set). Apps call ctx.icons.*
 * instead of bundling emoji or a second Lucide copy.
 */
import { createElement } from "lucide";
import {
  House,
  Monitor,
  FileText,
  Download,
  Image,
  Images,
  Film,
  Music,
  AppWindow,
  Share2,
  Trash2,
  Folder,
  FolderOpen,
  File,
  Code2,
  Archive,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  RefreshCw,
  Search,
  Scissors,
  Copy,
  ClipboardPaste,
  Clipboard,
  Pencil,
  FolderPlus,
  Upload,
  Info,
  Settings,
  X,
  MoreHorizontal,
  LayoutGrid,
  List,
  ChevronRight,
  Users,
  User,
  Terminal,
  Bot,
  Package,
  StickyNote,
  ScrollText,
  HardDrive,
  CircleHelp,
  FileCode,
  FileImage,
  FileArchive,
  FileAudio,
  FileVideo,
  Home,
  Activity,
  ChartLine,
  Save,
  Hammer,
  Play,
  Square,
  Rocket,
  Smartphone,
  Database,
  Table2,
  Inbox,
  ChevronLeft,
  ChevronDown,
  SlidersHorizontal,
  FilePlus,
  FolderTree,
  Sparkles,
  Bug,
  PanelLeft,
  PanelRight,
  Wand2,
  GitBranch,
  Check,
  Plus,
  Minus,
  Maximize2,
  Minimize2,
  Eye,
  Command,
  CircleCheck,
  CircleX,
  TriangleAlert,
  Clock,
  TrendingUp,
  CloudSun,
  // Hermes refusal panel — a missing id renders CircleHelp, not an error, so an
  // unregistered icon here would quietly turn the "add your key" panel into "?".
  KeyRound,
  UserX,
  Lock,
  LogIn,
  Timer,
  Hourglass,
  RotateCw,
  Shield,
  Layers,
} from "lucide";

export type IconOpts = {
  size?: number;
  strokeWidth?: number;
  className?: string;
  color?: string;
};

export type IconName = string;

/** Lucide icon nodes (arrays) — tree-shaken explicit set */
const REGISTRY: Record<string, unknown> = {
  House,
  Home,
  Monitor,
  FileText,
  Download,
  Image,
  Images,
  Film,
  Music,
  AppWindow,
  Share2,
  Trash2,
  Folder,
  FolderOpen,
  File,
  Code2,
  Archive,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  RefreshCw,
  Search,
  Scissors,
  Copy,
  ClipboardPaste,
  Clipboard,
  Pencil,
  FolderPlus,
  Upload,
  Info,
  Settings,
  X,
  MoreHorizontal,
  LayoutGrid,
  List,
  ChevronRight,
  Users,
  User,
  Terminal,
  Bot,
  Package,
  StickyNote,
  ScrollText,
  HardDrive,
  CircleHelp,
  FileCode,
  FileImage,
  FileArchive,
  FileAudio,
  FileVideo,
  Activity,
  ChartLine,
  Save,
  Hammer,
  Play,
  Square,
  Rocket,
  Smartphone,
  Database,
  Table2,
  Inbox,
  ChevronLeft,
  ChevronDown,
  SlidersHorizontal,
  FilePlus,
  FolderTree,
  Sparkles,
  Bug,
  PanelLeft,
  PanelRight,
  Wand2,
  GitBranch,
  Check,
  Plus,
  Minus,
  Maximize2,
  Minimize2,
  Eye,
  Command,
  CircleCheck,
  CircleX,
  TriangleAlert,
  Clock,
  TrendingUp,
  CloudSun,
  KeyRound,
  UserX,
  Lock,
  LogIn,
  Timer,
  Hourglass,
  RotateCw,
  Shield,
  Layers,
};

const ALIASES: Record<string, string> = {
  home: "House",
  house: "House",
  desktop: "Monitor",
  documents: "FileText",
  downloads: "Download",
  media: "Image",
  pictures: "Image",
  music: "Music",
  videos: "Film",
  apps: "AppWindow",
  applications: "LayoutGrid",
  sdk: "ScrollText",
  shared: "Share2",
  "shared-media": "Images",
  trash: "Trash2",
  folder: "Folder",
  "folder-open": "FolderOpen",
  file: "File",
  image: "Image",
  video: "Film",
  audio: "Music",
  code: "Code2",
  pdf: "FileText",
  archive: "Archive",
  back: "ArrowLeft",
  forward: "ArrowRight",
  up: "ArrowUp",
  refresh: "RefreshCw",
  search: "Search",
  cut: "Scissors",
  copy: "Copy",
  paste: "ClipboardPaste",
  clipboard: "Clipboard",
  rename: "Pencil",
  "new-folder": "FolderPlus",
  upload: "Upload",
  download: "Download",
  info: "Info",
  properties: "Info",
  settings: "Settings",
  close: "X",
  more: "MoreHorizontal",
  grid: "LayoutGrid",
  list: "List",
  chevron: "ChevronRight",
  users: "Users",
  user: "User",
  terminal: "Terminal",
  hermes: "Bot",
  package: "Package",
  packages: "Package",
  notes: "StickyNote",
  notepad: "StickyNote",
  files: "Folder",
  "release-notes": "ScrollText",
  harddisk: "HardDrive",
  computer: "Monitor",
  activity: "Activity",
  monitor: "Activity",
  chart: "ChartLine",
  charts: "ChartLine",
  analytics: "ChartLine",
  usage: "ChartLine",
  save: "Save",
  hammer: "Hammer",
  build: "Hammer",
  play: "Play",
  run: "Play",
  "refresh-cw": "RefreshCw",
  smartphone: "Smartphone",
  mobile: "Smartphone",
  tablet: "Smartphone",
  stop: "Square",
  deploy: "Rocket",
  launch: "Rocket",
  table: "Table2",
  "new-file": "FilePlus",
  "file-plus": "FilePlus",
  "sliders-horizontal": "SlidersHorizontal",
  sliders: "SlidersHorizontal",
  preferences: "SlidersHorizontal",
  wand: "Wand2",
  magic: "Wand2",
  git: "GitBranch",
  branch: "GitBranch",
  check: "Check",
  add: "Plus",
  plus: "Plus",
  minus: "Minus",
  remove: "Minus",
  sidebar: "PanelLeft",
  preview: "Eye",
  "explorer-tree": "FolderTree",
  command: "Command",
  palette: "Command",
  ok: "CircleCheck",
  error: "CircleX",
  warning: "TriangleAlert",
  problems: "Bug",
  clock: "Clock",
  time: "Clock",
  weather: "CloudSun",
  forecast: "CloudSun",
  trendingup: "TrendingUp",
  trending: "TrendingUp",
  compound: "TrendingUp",
  finance: "TrendingUp",
  stock: "TrendingUp",
  stocks: "TrendingUp",
  trading: "TrendingUp",
  invest: "TrendingUp",
  privacy: "Shield",
  gdpr: "Shield",
  retention: "Layers",
  rollup: "Layers",
  aggregate: "Layers",
};

function toPascal(name: string): string {
  const raw = (name || "").trim();
  if (!raw) return "CircleHelp";
  const lower = raw.toLowerCase();
  if (ALIASES[lower]) return ALIASES[lower];
  if (/^[A-Z][A-Za-z0-9]+$/.test(raw) && REGISTRY[raw]) return raw;
  const pascal = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .split(/[-_\s./]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
  if (REGISTRY[pascal]) return pascal;
  return "CircleHelp";
}

function resolveIconNode(name: string): unknown {
  const key = toPascal(name);
  return REGISTRY[key] || REGISTRY.CircleHelp;
}

function applySvgStyle(svg: SVGElement, opts: IconOpts) {
  const size = opts.size ?? 16;
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.style.width = size + "px";
  svg.style.height = size + "px";
  svg.style.flexShrink = "0";
  svg.style.display = "block";
  if (opts.className) svg.setAttribute("class", opts.className);
  if (opts.color) svg.style.color = opts.color;
  svg.style.pointerEvents = "none";
}

export function createIcon(name: IconName, opts: IconOpts = {}): SVGElement {
  const node = resolveIconNode(name);
  const svg = createElement(node as any, {
    strokeWidth: opts.strokeWidth ?? 1.75,
  }) as unknown as SVGElement;
  applySvgStyle(svg, opts);
  return svg;
}

export function iconHtml(name: IconName, opts: IconOpts = {}): string {
  return createIcon(name, opts).outerHTML;
}

export function resolveIconName(name: IconName): string {
  return toPascal(name);
}

export function hasIcon(name: IconName): boolean {
  const key = toPascal(name);
  return key !== "CircleHelp" || name.toLowerCase() === "circlehelp";
}

export function iconForEntry(
  entry: { name: string; type: "file" | "dir" },
  opts?: { trash?: boolean }
): IconName {
  if (opts?.trash) return "trash";
  if (entry.type === "dir") {
    const n = entry.name.toLowerCase();
    if (n === "desktop") return "desktop";
    if (n === "documents") return "documents";
    if (n === "downloads") return "downloads";
    if (n === "media" || n === "pictures" || n === "photos") return "media";
    if (n === "music") return "music";
    if (n === "videos" || n === "movies") return "videos";
    if (n === "apps" || n === "applications") return "apps";
    if (n === ".trash" || n === "trash") return "trash";
    return "folder";
  }
  const n = entry.name.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)$/.test(n)) return "FileImage";
  if (/\.(mp3|wav|flac|aac|m4a|ogg)$/.test(n)) return "FileAudio";
  if (/\.(mp4|mov|mkv|webm|avi)$/.test(n)) return "FileVideo";
  if (/\.(ts|tsx|js|jsx|mjs|cjs|py|rs|go|java|c|cpp|h|json|css|html|vue|svelte)$/.test(n))
    return "FileCode";
  if (/\.pdf$/.test(n)) return "pdf";
  if (/\.(zip|tar|gz|tgz|rar|7z)$/.test(n)) return "FileArchive";
  if (/\.(md|txt|log|rtf)$/.test(n)) return "documents";
  return "file";
}

export type IconsApi = {
  provider: "lucide";
  create: (name: IconName, opts?: IconOpts) => SVGElement;
  html: (name: IconName, opts?: IconOpts) => string;
  resolve: (name: IconName) => string;
  has: (name: IconName) => boolean;
  forEntry: (
    entry: { name: string; type: "file" | "dir" },
    opts?: { trash?: boolean; size?: number; strokeWidth?: number; color?: string }
  ) => SVGElement;
};

export function createIconsApi(): IconsApi {
  return {
    provider: "lucide",
    create: createIcon,
    html: iconHtml,
    resolve: resolveIconName,
    has: hasIcon,
    forEntry: (entry, opts) =>
      createIcon(iconForEntry(entry, { trash: opts?.trash }), {
        size: opts?.size ?? 18,
        strokeWidth: opts?.strokeWidth ?? 1.75,
        color: opts?.color,
      }),
  };
}
